import React, { useState, useEffect, useRef } from "react"
import policyPapersData from "../data/policy-papers-data.json"
import policyPapersFilters from "../data/policy-papers-filters.json"
import { slugify } from "../helpers/strings"
import { computedPxToNumber } from "../helpers/utils"
import VisibilitySensor from "react-visibility-sensor"

export default function PolicyPapers() {
  const [actionsFilter, setActionsFilter] = useState("all") // Accepts a filter slug
  const [actionsCollapsed, setActionsCollapsed] = useState(true)
  const [scrollTopVisible, setScrollTopVisible] = useState(false)

  // Process and store the board actions data *once* into datasets based on the filters
  var processedData = []

  policyPapersFilters.forEach(filter => {
    if (filter.slug) {
      if (filter.slug === "all") {
        processedData[filter.slug] = policyPapersData
      } else {
        processedData[filter.slug] = policyPapersData.filter(action =>
          action.categories.includes(filter.title)
        )
      }
    }
  })

  // Update the current dataset according to the actionsFilter state
  var filteredData = processedData[actionsFilter]

  //
  // Event Handlers
  //

  function handleSelectChange(e) {
    setActionsFilter(e.target.value)
  }

  // Hide the load more button if less than 7 items
  useEffect(() => {
    if (filteredData.length <= 6) {
      setActionsCollapsed(false)
    } else {
      setActionsCollapsed(true)
    }
  }, [filteredData.length])

  const onChange = isVisible => {
    setScrollTopVisible(isVisible)
  }

  return (
    <>
    <span className="anchor" id="policy-papers-anchor" tabIndex="-1" />
    <section
      id="policy-papers"
      className={
        actionsCollapsed
          ? `policy-papers policy-papers--truncated`
          : "policy-papers"
      }
    >
        <button
          className={`policy-papers__top ${(scrollTopVisible && !actionsCollapsed) && "policy-papers__top--visible"}`}
          onClick={() => document.querySelector(`#policy-papers-anchor`).scrollIntoView({ behavior: 'smooth' })}
        >
          Scroll up to policy papers
        </button>
      <div className="policy-papers__main">
        <header>
          <h2 className="title--xl">Policy Papers</h2>
          <div className="policy-papers__filter-wrapper">
            <header
              id="policy-papers__filter__header"
              className="title--micro title--light title--uppercase"
            >
              Filter By
            </header>
            
            <select
              name="filters"
              id="filters-select"
              className="policy-papers__filter"
              value={actionsFilter}
              onChange={handleSelectChange}
              aria-labelledby="policy-papers__filter__header"
              aria-label="Filter the board actions"
              aria-controls="policy-papers"
            >
              {policyPapersFilters.map((filter, index) => {
                return (
                  <option
                    key={"filter-select-" + filter.slug + index}
                    value={filter.slug}
                  >
                    {filter.title}
                  </option>
                )
              })}
            </select>
          </div>
        </header>
        <VisibilitySensor
          onChange={onChange}
          partialVisibility={true}
          offset={{ top: 600 }}
        >
        <ul className="policy-papers__items">
          {filteredData.map((action, index) => {
            // Action id must be totally unique or the list will not render/filter correctly
            let id = `policy-papers-item-${slugify(action.title)}-${slugify(
              action.date
            )}-index${index}`

            return (
              <PolicyPapersItem
                key={id}
                id={id}
                action={action}
                actionsCollapsed={actionsCollapsed}
              />
            )
          })}
          </ul>
          </VisibilitySensor>
        {actionsCollapsed && (
          <button
            className="cta cta--primary"
            onClick={() => setActionsCollapsed(false)}
          >
            Load More
          </button>
        )}
      </div>
      </section>
      </>
  )
}

function PolicyPapersItem({ id, action, actionsCollapsed }) {
  const contentRef = useRef(null)
  const contentPreviewClass = "policy-papers__items__item__content--preview"
  const [showContentPreview, setShowContentPreview] = useState(false)
  const [clampContent, setClampContent] = useState(false)
  const { date, categories, link, title, summary, press_release } = action

  // Initialize clamped state if content is too long
  useEffect(() => {
    const content = contentRef.current.querySelector(".text-block > p")
    const contentHeight = computedPxToNumber(content, "height")
    const contentLineHeight = computedPxToNumber(content, "lineHeight")

    if (!clampContent && !showContentPreview) {
      if (contentHeight > contentLineHeight * 3) {
        contentRef.current.classList.add(contentPreviewClass)
        setClampContent(true) // Only set once by design to prevent state reversion
        setShowContentPreview(true)
      }
    }
  }, [actionsCollapsed, clampContent, showContentPreview])

  // Listen to showContentPreview (i.e. button clicks) and show content if it has been clamped
  useEffect(() => {
    if (clampContent && !showContentPreview) {
      contentRef.current.classList.remove(contentPreviewClass)
    }
  }, [clampContent, showContentPreview])

  return (
    <li id={id} className="policy-papers__items__item">
      <div className="policy-papers__items__item__date">
        <p className="title--xs">{date}</p>
        <div className="policy-papers__items__item__date__line"></div>
      </div>
      <div ref={contentRef} className="policy-papers__items__item__content">
        <header>
          <p className="policy-papers__items__item__content__category title--xs">
            {categories}
          </p>
          <h3 className="title--r md:title--m">
            {link ? (
              <a href={link} target="_blank" rel="noreferrer">
                {title}
              </a>
            ) : (
              title
            )}
          </h3>
        </header>

        {summary && (
          <div className="text-block">
            <p>{summary}</p>
            {press_release && (
              <p>
                For more information, read the{" "}
                <a href={press_release} target="_blank" rel="noreferrer">
                  press release
                </a>
                .
              </p>
            )}
            <button
              type="button"
              onClick={() => setShowContentPreview(false)}
              aria-controls={id}
            >
              View More
            </button>
          </div>
        )}
      </div>
    </li>
  )
}
